'use client';

import {
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent,
  useEffect,
  useRef,
  useState,
} from 'react';

interface Ripple {
  id: number;
  style: CSSProperties;
}

export const useRipple = () => {
  const [ripples, setRipples] = useState<Ripple[]>([]);
  const countRef = useRef(0);
  const timeoutsRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  const createRipple = (
    event?: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>,
  ) => {
    const element = event?.currentTarget;
    if (!element) return;

    const rect = element.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);

    let x: number;
    let y: number;

    // Check if it's a MouseEvent (has clientX)
    if (event && 'clientX' in event) {
      x = event.clientX - rect.left - size / 2;
      y = event.clientY - rect.top - size / 2;
    } else {
      // Keyboard or Programmatic: Default to center
      x = rect.width / 2 - size / 2;
      y = rect.height / 2 - size / 2;
    }

    countRef.current += 1;
    const rippleId = countRef.current;

    const newRipple: Ripple = {
      id: rippleId,
      style: {
        height: `${size}px`,
        left: `${x}px`,
        top: `${y}px`,
        width: `${size}px`,
      },
    };

    setRipples(prev => [...prev, newRipple]);

    const timeoutId = setTimeout(() => {
      setRipples(prev => prev.filter(r => r.id !== rippleId));
      timeoutsRef.current.delete(timeoutId);
    }, 1000);

    timeoutsRef.current.add(timeoutId);
  };

  useEffect(() => {
    const activeTimeouts = timeoutsRef.current;
    return () => {
      activeTimeouts.forEach(id => clearTimeout(id));
      activeTimeouts.clear();
    };
  }, []);

  return {createRipple, ripples};
};
